import { IActivity, ICourse, ISection } from '@sdk/contracts';
import { useMemo } from 'react';

import { useMoveActivity } from '../../../store';
import { ActivityActions } from '../ActivityActions';
import { EditActivity } from '../EditActivity';
import { PreviewActivity } from '../PreviewActivity';

import { ActivityAuditStatus } from './ActivityAuditStatus';
import {
  ActivityStatus,
  Cover,
  Wrapper,
  ActivityNameWrapper,
  WrapperActivity,
  StyledCheckbox,
  SyncedIconWrapper,
  ActionsWrapper
} from './ActivityListItem.styles';

import { Icon } from '@/components/Icon';
import { SyncedIcon } from '@/components/SyncedIcon';
import { TaskStatusBadge } from '@/components/TaskStatusBadge';
import { ACTIVITY_ICONS } from '@/constants';
import { useProfile } from '@/store';
import { ECourseContentStep } from '@/types';

type Props = {
  activity: IActivity;
  section: ISection;
  course: ICourse;
};

export const ActivityListItem: React.FC<Props> = ({ activity, section, course }) => {
  const { data, setData } = useMoveActivity();
  const isChecked = useMemo(
    () => Boolean(data?.find(m => m._id === activity._id)),
    [data, activity]
  );
  const {
    profile,
    roleCheck: { isManager, isTeacher }
  } = useProfile();

  const isSyncedActivity = useMemo(
    () => activity.parentActivity || (activity.syncedActivities ?? []).length > 0,
    [activity]
  );

  const canEdit =
    !activity.parentActivity &&
    (isManager || (isTeacher && profile?._id === activity.task?.assignee?._id));

  const handleCheckboxChange = () => {
    if (isChecked) {
      return setData(data.filter(d => d._id !== activity._id));
    }
    setData([...data, activity]);
  };

  return (
    <WrapperActivity>
      {isManager && !activity.parentActivity && (
        <StyledCheckbox checked={isChecked} onClick={handleCheckboxChange} />
      )}
      <Wrapper>
        <Cover>
          <Icon i={ACTIVITY_ICONS[activity.type]} size={24} />
          {activity.resource ? <ActivityStatus active={activity.active} /> : <Icon i="Warning" />}

          {isSyncedActivity && (
            <SyncedIconWrapper>
              <SyncedIcon size={20} />
            </SyncedIconWrapper>
          )}
        </Cover>
        <ActivityNameWrapper>{activity.name}</ActivityNameWrapper>

        {activity.isLocked && <Icon i="Locked" />}

        <ActionsWrapper>
          <TaskStatusBadge status={activity.task?.statusInfo.currentStatus} />
          {!isSyncedActivity && <ActivityAuditStatus activity={activity} />}
          {canEdit && <EditActivity activity={activity} sectionId={section._id} course={course} />}
          <PreviewActivity activity={activity} />

          <ActivityActions
            target={ECourseContentStep.SECTIONS}
            section={section}
            activity={activity}
          />
        </ActionsWrapper>
      </Wrapper>
    </WrapperActivity>
  );
};
